import React, { useState } from "react";
import {
  BrandListItem,
  BrandListItemGroup,
  BrandListSkeleton,
} from "@App/Components/Vehicletree/BrandList";
import { SearchIcon } from "@App/Components/common/Icons";
import { Brand } from "@App/types";
import { useVehicletree } from "@App/hooks/api/useVehicletree";
import { useTranslation } from "react-i18next";
import { Button } from "@App/Components/Funnel/Button";

type BrandListProps = {
  onClick: (brand: Brand) => void;
};

export const BrandList = React.memo<BrandListProps>((props: BrandListProps) => {
  const { t } = useTranslation();

  const [more, setMore] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const { loading, brands, error } = useVehicletree(true);

  const groupBrandsByFirstChar = (brands: Brand[]) => {
    return brands.reduce((store, brand) => {
      const letter = brand.name.charAt(0);
      const keyStore =
        store[letter] || // Does it exist in the object?
        (store[letter] = []); // If not, create it as an empty array
      keyStore.push(brand);

      return store;
    }, {});
  };

  const getBrands = () => {
    const filteredBrands =
      brands &&
      groupBrandsByFirstChar(
        brands.filter((brand) => brand.name.toLowerCase().includes(searchTerm.toLowerCase())),
      );

    if (!filteredBrands) {
      return;
    }

    let rows = [];
    for (const key in filteredBrands) {
      rows.push(
        <BrandListItemGroup
          key={key}
          name={key}
          group={filteredBrands[key]}
          onClick={props.onClick}
        />,
      );
    }

    return rows;
  };

  const getImportantBrands = () => {
    return (
      brands &&
      brands
        .filter((brand) => brand.important === true)
        .map((brand) => <BrandListItem key={brand.id} brand={brand} onClick={props.onClick} />)
    );
  };

  const editSearchTerm = (e) => {
    setSearchTerm(e.target.value);
  };

  if (loading) {
    return <BrandListSkeleton />;
  }

  if (error) {
    return <div>{t("error")}</div>;
  }

  return (
    <div>
      <div className="relative md:w-80 max-w-full">
        <input
          value={searchTerm}
          type="text"
          placeholder={t("brandList.search-brand")}
          className="focus:outline-none w-full border focus:border-primary border-neutral-500 py-2 px-3 pr-8 rounded"
          onChange={editSearchTerm}
        />
        <SearchIcon className={"absolute top-2 right-2 w-6 h-6"} />
      </div>
      <div className={`mt-5 ${searchTerm ? "hidden" : "block"}`}>
        <span className="text-sm">{t("brandList.important-brands")}</span>
        {getImportantBrands()}
      </div>
      <div className={`mt-5 ${more || searchTerm ? "block" : "hidden"}`}>{getBrands()}</div>
      {!more && !searchTerm && (
        <Button onClick={() => setMore(true)} title={t("brandList.show-all-brands")} />
      )}
    </div>
  );
});
